/**
 * cardDisplay — pure helpers behind two result-card labels (launch audit Oct 26):
 *   - "Mkt Avg Odds" said -100 when every book was +100 (exactly 50% implied
 *     was mapped to the favourite side). Even money is +100.
 *   - "Time TBD" showed for games with a known kickoff: the card only read a
 *     few top-level date fields. It now reads every field the pipelines write
 *     and falls back to the ticker's fixture (by id, then by teams).
 */
import { impliedToAmerican, americanToDecimal } from './betMath';

/**
 * Total return (stake included) on `stake` at an American price, or null when
 * the price is missing or not a valid American price (0, ±50 …). SGPCard used
 * to divide by |price| and showed "$Infinity" for a stored price of 0.
 */
export function payoutForStake(stake, american) {
    if (american === null || american === undefined || american === '') return null;
    const dec = americanToDecimal(Number(american));
    if (dec == null || !Number.isFinite(stake)) return null;
    return stake * dec;
}

/**
 * Bet-slip totals. Same-game legs are an SGP: books price it as one parlay
 * with correlation, so the payout comes ONLY from the SGP price (`sgpPrice`);
 * without it there is no payout (null), never the product of the legs. Legs
 * from different games pay the product of the legs, and only when every leg
 * has a valid price (a missing leg is never filled with -110).
 *
 * In "straight" mode every leg is its own bet at `stake`: the payout is the
 * sum of the legs' returns (stake × 2 legs at +100 → $40 total), and only
 * when every leg is priced.
 *
 * @param {{ legs: Array, stake: number, sgpPrice?: number|null, betMode?: string }} p
 * @returns {{ payout: number|null, decimal: number|null, isSgp: boolean, straight?: boolean }}
 */
export function betSlipPayout({ legs, stake, sgpPrice = null, betMode = 'infer' }) {
    const list = Array.isArray(legs) ? legs : [];
    if (betMode === 'straight') {
        let total = 0;
        for (const l of list) {
            const raw = l?.raw || {};
            const p = raw.price ?? raw.american_price ?? raw.odds ?? null;
            const d = p == null ? null : americanToDecimal(Number(p));
            if (d == null) return { payout: null, decimal: null, isSgp: false, straight: true, totalStake: stake * list.length };
            total += stake * d;
        }
        return { payout: list.length ? total : null, decimal: null, isSgp: false, straight: true, totalStake: stake * list.length };
    }
    const fixtures = new Set(list.map((l) => l?.raw?.fixture_id || l?.raw?.game_id || null).filter(Boolean));
    const isSgp = list.length >= 2 && fixtures.size === 1;
    if (isSgp) {
        const dec = sgpPrice == null ? null : americanToDecimal(Number(sgpPrice));
        return { payout: dec == null ? null : stake * dec, decimal: dec, isSgp };
    }
    if (list.length === 0) return { payout: null, decimal: null, isSgp };
    let dec = 1;
    for (const l of list) {
        const raw = l?.raw || {};
        const p = raw.price ?? raw.american_price ?? raw.odds ?? null;
        const d = p == null ? null : americanToDecimal(Number(p));
        if (d == null) return { payout: null, decimal: null, isSgp };
        dec *= d;
    }
    return { payout: stake * dec, decimal: dec, isSgp };
}

/**
 * /parlay/odds entries for a bet slip, or null when any leg lacks a valid
 * American price — the quote is then unavailable; no -110 is ever invented.
 * Legs without a fixture_id are left out (the endpoint needs one).
 */
export function parlayQuoteEntries(legs) {
    const list = Array.isArray(legs) ? legs : [];
    const priceOf = (l) => {
        const raw = l?.raw || {};
        const p = raw.price ?? raw.american_price ?? raw.odds;
        return p == null || p === '' ? null : Number(p);
    };
    if (list.some((l) => americanToDecimal(priceOf(l)) == null)) return null;
    return list.map((l) => {
        const raw = l?.raw || {};
        return {
            market: raw.market || 'Unknown',
            name: raw.name || raw.selection || l?.title || 'Unknown',
            fixture_id: raw.fixture_id || null,
            price_american: priceOf(l),
        };
    }).filter((e) => e.fixture_id);
}

/**
 * Profit on a bet slip: total return minus everything staked. Straight mode
 * stakes `stake` on each leg (slip.totalStake), so 2 legs at +100 with $100
 * each return $400 for $200 staked: $200 profit, not $300.
 */
export function betSlipProfit(slip, stake) {
    if (!slip || slip.payout == null) return null;
    const staked = Number.isFinite(slip.totalStake) ? slip.totalStake : stake;
    return slip.payout - staked;
}

/** Bet-slip payout line: never a product-of-legs payout for a same-game slip. */
export function betSlipPayoutLabel(slip) {
    if (!slip || slip.payout == null) return slip?.isSgp ? 'SGP price unavailable' : 'To Win —';
    return `To Win $${Math.round(slip.payout)}`;
}

const _legKey = (market, selection) =>
    `${String(market || '').trim().toLowerCase()}|${String(selection || '').trim().toLowerCase()}`;

/**
 * The SGP price (American) for a bet slip: the combined price of a fetched SGP
 * whose legs are exactly the selected legs (same fixture, same market +
 * selection set). Uses combined_price, or combined_decimal converted. Null
 * when no SGP matches — the slip then shows "SGP price unavailable".
 */
export function findMatchingSgpPrice(selectedLegs, options) {
    const legs = Array.isArray(selectedLegs) ? selectedLegs : [];
    if (legs.length < 2 || !Array.isArray(options)) return null;
    const want = legs.map((l) => {
        const r = l?.raw || {};
        return _legKey(r.market, r.selection ?? r.name ?? l?.title);
    }).sort();
    const fixture = legs[0]?.raw?.fixture_id || legs[0]?.raw?.game_id || null;
    for (const opt of options) {
        const raw = opt?.raw || opt || {};
        const sgpLegs = Array.isArray(raw.legs) ? raw.legs : null;
        if (!sgpLegs || sgpLegs.length !== want.length) continue;
        if (fixture && raw.fixture_id && raw.fixture_id !== fixture) continue;
        const have = sgpLegs.map((l) => _legKey(l?.market, l?.selection)).sort();
        if (have.some((k, i) => k !== want[i])) continue;
        const am = raw.combined_price ?? raw.price ?? null;
        if (am != null && americanToDecimal(Number(am)) != null) return Number(am);
        const dec = Number(raw.combined_decimal);
        if (Number.isFinite(dec) && dec > 1) {
            const a = impliedToAmerican(1 / dec);
            return a == null ? null : Math.round(a);
        }
        return null;
    }
    return null;
}

/** "+3.2%" / "-1.5%"; a value that rounds to zero is "0.0%", never "-0.0%". */
export function formatEvPercent(ev, digits = 1) {
    if (ev === null || ev === undefined || !Number.isFinite(ev)) return null;
    let s = ev.toFixed(digits);
    if (Number(s) === 0) s = (0).toFixed(digits);
    return Number(s) > 0 ? `+${s}%` : `${s}%`;
}

/** Average a list of American prices in implied-probability space, back to American. */
export function marketAverageAmerican(prices) {
    const valid = (prices || [])
        .map((p) => (p == null || p === '' ? NaN : Number(p)))
        .filter((p) => Number.isFinite(p) && (Math.abs(p) >= 100 || p === 0));
    if (valid.length === 0) return null;
    const imps = valid.map((p) => (p > 0 ? 100 / (p + 100) : Math.abs(p) / (Math.abs(p) + 100)));
    const avg = imps.reduce((a, b) => a + b, 0) / imps.length;
    // Float noise around exactly 50% (e.g. +100/-100 pairs) must still read as even money.
    if (Math.abs(avg - 0.5) < 1e-9) return 100;
    const am = impliedToAmerican(avg);
    return am == null ? null : Math.round(am);
}

/** Firestore timestamp ({_seconds} / {seconds}), ISO string, epoch ms or Date → Date | null. */
export function parseKickoff(value) {
    if (value == null || value === '') return null;
    try {
        if (typeof value === 'object' && !(value instanceof Date)) {
            const s = value._seconds ?? value.seconds;
            if (Number.isFinite(s)) return new Date(s * 1000);
            return null;
        }
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
}

const DATE_FIELDS = ['start_date', 'commence_time', 'event_date', 'game_time', 'start_time', '_start_date', 'startDate'];

function fromFields(obj) {
    if (!obj || typeof obj !== 'object') return null;
    for (const f of DATE_FIELDS) {
        const d = parseKickoff(obj[f]);
        if (d) return d;
    }
    return null;
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Kickoff for a result card: the row's own fields (top level, then nested fixture),
 * then the ticker fixture with the same id, then one with the same two teams.
 */
export function resolveCardKickoff(raw, workingOption, fixtures) {
    const r = raw || {};
    const w = workingOption || {};
    const own = fromFields(r)
        || fromFields(r._fixture) || fromFields(r.fixture)
        || parseKickoff(w.date) || fromFields(w) || fromFields(w.raw)
        || fromFields(w.raw?._fixture) || fromFields(w.raw?.fixture);
    if (own) return own;
    if (!Array.isArray(fixtures) || fixtures.length === 0) return null;
    const fid = r.fixture_id || w.fixture_id || w.raw?.fixture_id;
    if (fid) {
        const hit = fixtures.find((f) => f && (f.fixture_id === fid || f.id === fid));
        const d = hit && parseKickoff(hit.start_date);
        if (d) return d;
    }
    const home = norm(r.home_team || w.home_team || w.raw?.home_team);
    const away = norm(r.away_team || w.away_team || w.raw?.away_team);
    if (home && away) {
        // Teams alone are ambiguous in a series (game 2 vs game 3): use them only
        // when exactly one ticker fixture matches (and the league agrees when known).
        const league = norm(r.league || w.league || w.raw?.league);
        const hits = fixtures.filter((f) => f && norm(f.home_team) === home && norm(f.away_team) === away
            && (!league || !f.league || norm(f.league) === league));
        const d = hits.length === 1 && parseKickoff(hits[0].start_date);
        if (d) return d;
    }
    return null;
}
